import dayjs from 'dayjs';
import parsePhoneNumberFromString from 'libphonenumber-js';

import {
  MAX_CURRENT_GA_DAYS,
  MAX_CURRENT_GA_TOTAL_DAYS,
  MAX_CURRENT_GA_WEEKS,
} from '../constants';
import type {
  CoreData,
  CurrentPregnancyData,
  PriorDeliveryRowState,
  TabId,
} from '../types';
import { tabForFieldError } from './errorMapping';

export interface ValidationResult {
  errors: Record<string, string>;
  firstErrorTab: TabId | null;
}

type Translator = (key: string, options?: Record<string, unknown>) => string;

export const validateDossierForm = (
  core: CoreData,
  priorDeliveries: PriorDeliveryRowState[],
  pregnancy: CurrentPregnancyData,
  t: Translator
): ValidationResult => {
  const errors: Record<string, string> = {};
  const gaRangeMessage = t('dossier_current_ga_range_error', {
    maxWeeks: MAX_CURRENT_GA_WEEKS,
    maxDays: MAX_CURRENT_GA_DAYS,
  });
  const gaDateRangeMessage = t('dossier_current_pregnancy_date_range_error', {
    maxWeeks: MAX_CURRENT_GA_WEEKS,
    maxDays: MAX_CURRENT_GA_DAYS,
  });

  if (!core.patient_name.trim()) {
    errors.patient_name = t('firstNameRequired');
  }
  if (!core.patient_last_name.trim()) {
    errors.patient_last_name = t('lastNameRequired');
  }

  if (core.email.trim()) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(core.email.trim())) {
      errors.email = t('validEmailRequired');
    }
  }

  if (core.phone_number) {
    try {
      const parsed = parsePhoneNumberFromString(core.phone_number);
      if (!parsed || !parsed.isValid()) {
        errors.phone_number = t('invalidPhoneNumber');
      }
    } catch {
      errors.phone_number = t('invalidPhoneNumber');
    }
  }

  if (core.birth_date && new Date(core.birth_date) > new Date()) {
    errors.birth_date = t('futureDateError');
  }

  if (pregnancy.ga_weeks.trim()) {
    const weeks = Number(pregnancy.ga_weeks);
    if (
      !/^\d+$/.test(pregnancy.ga_weeks) ||
      !Number.isFinite(weeks) ||
      weeks < 0 ||
      weeks > MAX_CURRENT_GA_WEEKS
    ) {
      errors.ga_weeks = gaRangeMessage;
    }
  }

  if (pregnancy.ga_days.trim()) {
    const days = Number(pregnancy.ga_days);
    if (
      !/^\d+$/.test(pregnancy.ga_days) ||
      !Number.isFinite(days) ||
      days < 0 ||
      days > MAX_CURRENT_GA_DAYS
    ) {
      errors.ga_days = gaRangeMessage;
    }
  }

  if (pregnancy.last_menstrual_period) {
    const lmp = dayjs(pregnancy.last_menstrual_period).startOf('day');
    if (lmp.isValid()) {
      const totalDays = dayjs().startOf('day').diff(lmp, 'day');
      if (totalDays < 0 || totalDays > MAX_CURRENT_GA_TOTAL_DAYS) {
        errors.last_menstrual_period = gaDateRangeMessage;
      }
    }
  }

  if (pregnancy.estimated_due_date) {
    const edd = dayjs(pregnancy.estimated_due_date).startOf('day');
    if (edd.isValid()) {
      const derivedLmp = edd.subtract(280, 'day');
      const totalDays = dayjs().startOf('day').diff(derivedLmp, 'day');
      if (totalDays < 0 || totalDays > MAX_CURRENT_GA_TOTAL_DAYS) {
        errors.estimated_due_date = gaDateRangeMessage;
      }
    }
  }

  let firstAnomalyMissingIdx = -1;
  priorDeliveries.forEach((row, idx) => {
    if (row.has_obstetric_anomalies && !row.anomaly_type) {
      errors[`prior_delivery_${idx}_anomaly_type`] = t(
        'dossier_anomaly_type_required'
      );
      if (firstAnomalyMissingIdx === -1) firstAnomalyMissingIdx = idx;
    }
  });

  let firstErrorTab: TabId | null = null;
  if (Object.keys(errors).length > 0) {
    const firstField = Object.keys(errors)[0];
    firstErrorTab =
      firstAnomalyMissingIdx >= 0 && firstField.startsWith('prior_delivery_')
        ? 'accouchement'
        : tabForFieldError(firstField);
  }

  return { errors, firstErrorTab };
};
